import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  IconButton,
  Tooltip as MuiTooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import html2canvas from 'html2canvas';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
} from 'recharts';
import { behaviorStatsAPI } from '../../services/api';
import {
  DEFAULT_PLOT_WIDTH_PX,
  formatSwingTickDate,
  resolveReadableAxisTicks,
} from './vnindexAxisTickLayout';

const RANGE_OPTIONS = [
  { value: '1Y', years: 1, limit: 280 },
  { value: '3Y', years: 3, limit: 850 },
  { value: '5Y', years: 5, limit: 1400 },
  { value: 'ALL', years: null, limit: 2500 },
];

const ALL_RANGE_START = '2015-01-01';

const getSpanDays = (points) => {
  if (!points?.length) return 0;
  const firstDate = new Date(points[0].trade_date);
  const lastDate = new Date(points[points.length - 1].trade_date);
  return Math.max(1, Math.round((lastDate - firstDate) / (1000 * 60 * 60 * 24)));
};

/** Fix occasional 10x-inflated VNINDEX closes that break the left Y-axis scale. */
const sanitizeVnindexPrices = (points) => {
  if (!points?.length) return [];

  return points.map((point, index) => {
    const close = point.vnindex_close;
    if (close == null) return point;

    const window = points.slice(Math.max(0, index - 5), Math.min(points.length, index + 6));
    const neighbors = window
      .map((p) => p.vnindex_close)
      .filter((value) => value != null && value < 2500)
      .sort((a, b) => a - b);

    const median = neighbors.length
      ? neighbors[Math.floor(neighbors.length / 2)]
      : close;

    const isOutlier = close > 2500 || (median > 0 && close > median * 3);
    if (!isOutlier) return point;

    return { ...point, vnindex_close: close / 10 };
  });
};

const getDisplayConfig = (spanDays) => {
  if (spanDays > 1800) {
    return {
      maxPoints: 180,
      showSwingMarkers: true,
      maxSwingMarkers: 28,
      chartHeight: 420,
      aggregationLabel: 'monthly',
      panicStrokeWidth: 1,
      fomoStrokeWidth: 1,
      vnindexStrokeWidth: 2,
    };
  }

  if (spanDays > 900) {
    return {
      maxPoints: 220,
      showSwingMarkers: true,
      maxSwingMarkers: 32,
      chartHeight: 400,
      aggregationLabel: 'weekly',
      panicStrokeWidth: 1.25,
      fomoStrokeWidth: 1.25,
      vnindexStrokeWidth: 2,
    };
  }

  if (spanDays > 400) {
    return {
      maxPoints: 280,
      showSwingMarkers: true,
      maxSwingMarkers: 36,
      chartHeight: 380,
      aggregationLabel: 'weekly',
      panicStrokeWidth: 1.25,
      fomoStrokeWidth: 1.25,
      vnindexStrokeWidth: 2,
    };
  }

  return {
    maxPoints: null,
    showSwingMarkers: true,
    maxSwingMarkers: null,
    chartHeight: 360,
    aggregationLabel: null,
    panicStrokeWidth: 1.5,
    fomoStrokeWidth: 1.5,
    vnindexStrokeWidth: 2,
  };
};

const downsamplePoints = (points, maxPoints) => {
  if (!maxPoints || points.length <= maxPoints) return points;

  const bucketSize = Math.ceil(points.length / maxPoints);
  const result = [];

  for (let i = 0; i < points.length; i += bucketSize) {
    const bucket = points.slice(i, i + bucketSize);
    const last = bucket[bucket.length - 1];
    const panicValues = bucket.map((p) => p.panic_score).filter((v) => v != null);
    const fomoValues = bucket.map((p) => p.fomo_score).filter((v) => v != null);
    const swingPoint = bucket.find((p) => p.swing_type);

    result.push({
      ...last,
      panic_score: panicValues.length
        ? Math.max(...panicValues)
        : last.panic_score,
      fomo_score: fomoValues.length
        ? Math.max(...fomoValues)
        : last.fomo_score,
      swing_type: swingPoint?.swing_type ?? null,
    });
  }

  return result;
};

const getVnindexDomain = (points) => {
  const closes = points
    .map((p) => p.vnindex_close)
    .filter((value) => value != null)
    .sort((a, b) => a - b);

  if (!closes.length) return ['auto', 'auto'];

  const low = closes[Math.floor(closes.length * 0.02)];
  const high = closes[Math.floor(closes.length * 0.98)];
  const padding = Math.max((high - low) * 0.06, 20);

  return [Math.floor(low - padding), Math.ceil(high + padding)];
};

const extractSwingMarkers = (points, maxMarkers) => {
  const bottoms = points
    .filter((p) => p.swing_type === 'bottom')
    .sort((a, b) => (b.panic_score ?? 0) - (a.panic_score ?? 0));
  const tops = points
    .filter((p) => p.swing_type === 'top')
    .sort((a, b) => (b.fomo_score ?? 0) - (a.fomo_score ?? 0));

  if (!maxMarkers) {
    return [...bottoms, ...tops].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
  }

  return [
    ...bottoms.slice(0, Math.ceil(maxMarkers / 2)),
    ...tops.slice(0, Math.floor(maxMarkers / 2)),
  ].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
};

const injectPinnedDates = (chartPoints, sourcePoints, pinnedDates) => {
  if (!pinnedDates?.length) return chartPoints;

  const existing = new Set(chartPoints.map((point) => point.trade_date));
  const sourceMap = new Map(sourcePoints.map((point) => [point.trade_date, point]));
  const injected = [...chartPoints];

  pinnedDates.forEach((tradeDate) => {
    if (existing.has(tradeDate)) return;
    const source = sourceMap.get(tradeDate);
    if (!source) return;
    injected.push({ ...source, swing_type: source.swing_type ?? null });
    existing.add(tradeDate);
  });

  return injected.sort((a, b) => a.trade_date.localeCompare(b.trade_date));
};

const getAxisTickPolicy = (spanDays) => {
  if (spanDays > 1800) {
    return { minGapDays: 18, priorityLimit: 5, staggerGapDays: 20 };
  }
  if (spanDays > 900) {
    return { minGapDays: 14, priorityLimit: 4, staggerGapDays: 16 };
  }
  if (spanDays > 400) {
    return { minGapDays: 12, priorityLimit: 3, staggerGapDays: 14 };
  }
  return { minGapDays: 10, priorityLimit: 2, staggerGapDays: 12 };
};

const scoreTickDate = (tradeDate, swingMarkers, priorityMarkers, sanitized) => {
  const point = sanitized.find((item) => item.trade_date === tradeDate);
  const swing = swingMarkers.find((item) => item.trade_date === tradeDate);
  const priority = priorityMarkers.find((item) => item.trade_date === tradeDate);

  let score = 0;
  if (swing?.swing_type === 'bottom') {
    score += 1000 + (swing.panic_score ?? 0) * 80;
  }
  if (swing?.swing_type === 'top') {
    score += 1000 + (swing.fomo_score ?? 0) * 80;
  }
  if (priority) {
    score += 250 + Math.max(priority.panic_score ?? 0, priority.fomo_score ?? 0) * 120;
  }
  if (point?.panic_score != null) {
    score += point.panic_score * 20;
  }
  if (point?.fomo_score != null) {
    score += point.fomo_score * 10;
  }

  return score;
};

export const getPriorityExtremeMarkers = (points, spanDays) => {
  if (!points?.length) return [];

  const { priorityLimit } = getAxisTickPolicy(spanDays);
  const topPanic = [...points]
    .filter((point) => point.panic_score != null)
    .sort((a, b) => (b.panic_score ?? 0) - (a.panic_score ?? 0))
    .slice(0, priorityLimit);
  const topFomo = [...points]
    .filter((point) => point.fomo_score != null)
    .sort((a, b) => (b.fomo_score ?? 0) - (a.fomo_score ?? 0))
    .slice(0, priorityLimit);

  const merged = new Map();
  [...topPanic, ...topFomo].forEach((point) => {
    merged.set(point.trade_date, point);
  });

  return [...merged.values()].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
};

export const buildLabelMetaByDate = (swingMarkers, priorityMarkers) => {
  const meta = {};

  swingMarkers.forEach((point) => {
    meta[point.trade_date] = { kind: point.swing_type };
  });

  priorityMarkers.forEach((point) => {
    if (meta[point.trade_date]) return;

    const panic = point.panic_score ?? 0;
    const fomo = point.fomo_score ?? 0;
    meta[point.trade_date] = {
      kind: panic >= fomo ? 'panic_extreme' : 'fomo_extreme',
    };
  });

  return meta;
};

export const getXAxisTicks = (
  chartPoints,
  swingMarkers = [],
  spanDays = 0,
  sanitized = [],
  plotWidthPx = DEFAULT_PLOT_WIDTH_PX,
) => {
  if (!chartPoints?.length) return [];

  const sourcePoints = sanitized.length ? sanitized : swingMarkers;
  const priorityMarkers = getPriorityExtremeMarkers(sourcePoints, spanDays);
  const swingDates = swingMarkers.map((marker) => marker.trade_date);
  const priorityDates = priorityMarkers
    .map((marker) => marker.trade_date)
    .filter((tradeDate) => !swingDates.includes(tradeDate));

  return resolveReadableAxisTicks({
    candidateDates: [...swingDates, ...priorityDates],
    chartPoints,
    spanDays,
    plotWidthPx,
    getScore: (tradeDate) => scoreTickDate(
      tradeDate,
      swingMarkers,
      priorityMarkers,
      sourcePoints,
    ),
  });
};

export const formatXAxisTick = (value, spanDays, { isSwing = false } = {}) => (
  formatSwingTickDate(value, spanDays, { compact: isSwing })
);

const formatDateInput = (date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const getRangeDates = (rangeValue) => {
  const endDate = formatDateInput(new Date());
  const option = RANGE_OPTIONS.find((item) => item.value === rangeValue) || RANGE_OPTIONS[1];

  if (!option.years) {
    return { startDate: ALL_RANGE_START, endDate, limit: option.limit };
  }

  const start = new Date();
  start.setFullYear(start.getFullYear() - option.years);
  return { startDate: formatDateInput(start), endDate, limit: option.limit };
};

const TICK_LABEL_COLORS = {
  bottom: '#43a047',
  top: '#e53935',
  panic_extreme: '#fb8c00',
  fomo_extreme: '#00acc1',
};

const getTickLabelColor = (kind, fallbackColor) => TICK_LABEL_COLORS[kind] ?? fallbackColor;

const getSwingMarkerRadius = (spanDays) => {
  if (spanDays > 900) return 3.5;
  if (spanDays > 400) return 4;
  return 5;
};

const VNIndexTooltip = ({ active, payload, theme, t }) => {
  if (!active || !payload || payload.length === 0) return null;

  const point = payload[0].payload;
  return (
    <Box
      sx={{
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        padding: '10px 15px',
        borderRadius: '6px',
        border: `1px solid ${theme.palette.primary.main}`,
      }}
    >
      <Typography variant="caption" sx={{ color: '#fff', display: 'block', marginBottom: '6px' }}>
        {point.trade_date}
      </Typography>

      <Typography variant="caption" sx={{ color: theme.palette.info.light }}>
        {t('sentiment.behavior.vnindex.tooltip_price', 'Price')}: {point.vnindex_close?.toFixed(2)} ₫
      </Typography>
      <br />

      <Typography variant="caption" sx={{ color: '#ff9800' }}>
        {t('sentiment.behavior.signal.panic', 'Panic Score')}: {((point.panic_score ?? 0) * 100).toFixed(1)}%
      </Typography>
      <br />

      <Typography variant="caption" sx={{ color: '#4caf50' }}>
        {t('sentiment.behavior.signal.fomo', 'FOMO Score')}: {((point.fomo_score ?? 0) * 100).toFixed(1)}%
      </Typography>
      <br />

      {point.swing_type && (
        <>
          <Typography
            variant="caption"
            sx={{
              color: point.swing_type === 'bottom' ? '#4caf50' : '#f44336',
              fontWeight: 'bold',
            }}
          >
            {point.swing_type === 'bottom'
              ? t('sentiment.behavior.vnindex.marker_bottom', 'Bottom')
              : t('sentiment.behavior.vnindex.marker_top', 'Top')}
          </Typography>
          <br />
        </>
      )}

      {point.action_level && (
        <Typography variant="caption" sx={{ color: '#fff', fontStyle: 'italic' }}>
          {point.action_level}
        </Typography>
      )}
    </Box>
  );
};

VNIndexTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.arrayOf(PropTypes.shape({
    payload: PropTypes.shape({
      trade_date: PropTypes.string,
      vnindex_close: PropTypes.number,
      panic_score: PropTypes.number,
      fomo_score: PropTypes.number,
      swing_type: PropTypes.string,
      action_level: PropTypes.string,
    }),
  })),
  theme: PropTypes.shape({
    palette: PropTypes.shape({
      primary: PropTypes.shape({ main: PropTypes.string }),
      info: PropTypes.shape({ light: PropTypes.string }),
    }),
  }).isRequired,
  t: PropTypes.func.isRequired,
};

VNIndexTooltip.defaultProps = {
  active: false,
  payload: [],
};

/**
 * VNIndexCorrelationChart
 *
 * Displays historical VNINDEX price action with panic/fomo scores.
 * Detects swing lows (market bottoms) and highs (market tops) to show
 * correlation between fear/greed and price extremes.
 */
const VNIndexCorrelationChart = ({
  refreshTrigger = 0,
  defaultRange = '1Y',
  calculating = false,
  onCalculate,
  t,
}) => {
  const theme = useTheme();
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const exportRef = useRef(null);
  const [exportingImage, setExportingImage] = useState(false);
  const handleExportImage = async () => {
    if (!exportRef.current || exportingImage) return;
    try {
      setExportingImage(true);
      const canvas = await html2canvas(exportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const asOfDate = new Date().toISOString().slice(0, 10);
      const link = document.createElement('a');
      link.download = `vnindex-market-psychology-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingImage(false);
    }
  };

  const { startDate, endDate, limit } = useMemo(() => getRangeDates(range), [range]);

  const renderRightAxisLabel = ({ viewBox }) => {
    if (!viewBox) return null;

    const labelX = viewBox.x + viewBox.width + 16;
    const labelY = viewBox.y + (viewBox.height / 2);

    return (
      <text
        x={labelX}
        y={labelY}
        fill={theme.palette.text.secondary}
        fontSize="13"
        fontWeight="600"
        textAnchor="middle"
        dominantBaseline="middle"
        transform={`rotate(90, ${labelX}, ${labelY})`}
      >
        {t('sentiment.behavior.vnindex.axis_sentiment', 'Panic / FOMO (%)')}
      </text>
    );
  };

  const getXAxisTicksForChart = (points, swingMarkers, chartSpanDays, sanitizedPoints) =>
    getXAxisTicks(points, swingMarkers, chartSpanDays, sanitizedPoints);

  const formatXAxisTickForChart = (value, chartSpanDays, isSwing = false) =>
    formatXAxisTick(value, chartSpanDays, { isSwing });

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (data) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }
        setError(null);

        const response = await behaviorStatsAPI.getVNIndexCorrelation(
          startDate,
          endDate,
          limit,
          10,
        );

        setData(response);
      } catch (err) {
        console.error('Error fetching VNINDEX correlation:', err);
        setError('sentiment.behavior.vnindex.error_load');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- data intentionally excluded; only refetch on range/trigger change
  }, [startDate, endDate, limit, refreshTrigger]);

  const chartPoints = useMemo(() => {
    const dataPoints = data?.data_points;
    if (!dataPoints?.length) {
      return {
        points: [],
        swingMarkers: [],
        sanitized: [],
        spanDays: 0,
        displayConfig: getDisplayConfig(0),
      };
    }

    const sanitized = sanitizeVnindexPrices(dataPoints);
    const spanDays = getSpanDays(sanitized);
    const displayConfig = getDisplayConfig(spanDays);
    const swingMarkers = displayConfig.showSwingMarkers
      ? extractSwingMarkers(sanitized, displayConfig.maxSwingMarkers)
      : [];
    const points = downsamplePoints(sanitized, displayConfig.maxPoints).map((point) => (
      { ...point, swing_type: null }
    ));

    return {
      points,
      swingMarkers,
      sanitized,
      spanDays,
      displayConfig,
    };
  }, [data?.data_points]);

  const rangeSelector = (
    <ToggleButtonGroup
      value={range}
      exclusive
      size="small"
      onChange={(_, value) => value && setRange(value)}
      sx={{
        flexShrink: 0,
        '& .MuiToggleButton-root': {
          textTransform: 'none',
          fontSize: '0.72rem',
          fontWeight: 600,
          px: 1.25,
          py: 0.25,
        },
      }}
    >
      {RANGE_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          {t(`sentiment.behavior.vnindex.range_${option.value.toLowerCase()}`, option.value)}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );

  const summaryItems = useMemo(() => {
    if (!data) return [];

    return [
      {
        label: t('sentiment.behavior.vnindex.bottoms_detected', 'Bottoms detected'),
        value: data.bottoms_detected,
        color: '#43a047',
      },
      {
        label: t('sentiment.behavior.vnindex.tops_detected', 'Tops detected'),
        value: data.tops_detected,
        color: '#f44336',
      },
      {
        label: t('sentiment.behavior.vnindex.avg_panic_at_bottoms', 'Avg Panic at Bottoms'),
        value: data.avg_panic_at_bottoms != null
          ? `${(data.avg_panic_at_bottoms * 100).toFixed(1)}%`
          : t('sentiment.behavior.vnindex.na', 'N/A'),
        color: '#fb8c00',
      },
      {
        label: t('sentiment.behavior.vnindex.avg_fomo_at_tops', 'Avg FOMO at Tops'),
        value: data.avg_fomo_at_tops != null
          ? `${(data.avg_fomo_at_tops * 100).toFixed(1)}%`
          : t('sentiment.behavior.vnindex.na', 'N/A'),
        color: '#00acc1',
      },
    ];
  }, [data, t]);

  const renderHeader = () => (
    <Box
      sx={{
        px: 2,
        pt: 1.5,
        pb: 1,
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 1.5,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ minWidth: 0, flex: '1 1 200px' }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: 'text.primary', lineHeight: 1.3 }}>
          {t('sentiment.behavior.vnindex.title', 'VNINDEX & Market Psychology')}
        </Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mt: 0.2, lineHeight: 1.35 }}>
          {t('sentiment.behavior.vnindex.subtitle', 'Correlation between index price and fear/excitement')}
        </Typography>
      </Box>

      {!loading && !error && data && summaryItems.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: { xs: 2, sm: 2.5 },
            flexWrap: 'wrap',
            flex: '1 1 320px',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          {summaryItems.map((item) => (
            <Box
              key={item.label}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 0.15,
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                }}
              >
                {item.label}
              </Typography>
              <Typography sx={{ color: item.color, fontWeight: 800, fontSize: '0.9rem', lineHeight: 1.1 }}>
                {item.value}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ flexShrink: 0, ml: { md: 'auto' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {(refreshing || calculating) && <CircularProgress size={16} thickness={5} />}
        {rangeSelector}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-html2canvas-ignore="true">
          {onCalculate && (
            <MuiTooltip title={calculating ? t('sentiment.behavior.vnindex.calculating', 'Calculating...') : t('sentiment.behavior.vnindex.calculate', 'Recalculate')}>
              <span>
                <IconButton size="small" onClick={onCalculate} disabled={calculating || loading}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </MuiTooltip>
          )}
          <MuiTooltip title={exportingImage ? t('sentiment.behavior.vnindex.exportingImage', 'Exporting image...') : t('sentiment.behavior.vnindex.exportImage', 'Export image')}>
            <span>
              <IconButton size="small" onClick={handleExportImage} disabled={exportingImage || loading}>
                {exportingImage ? <CircularProgress size={16} /> : <FileDownloadOutlinedIcon fontSize="small" />}
              </IconButton>
            </span>
          </MuiTooltip>
        </Box>
      </Box>
    </Box>
  );

  if (loading) {
    return (
      <Card ref={exportRef}>
        {renderHeader()}
        <CardContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', pt: 0 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card ref={exportRef}>
        {renderHeader()}
        <CardContent sx={{ pt: 0 }}>
          <Alert severity="error">{error ? t(error, error) : t('sentiment.behavior.vnindex.empty_data', 'No data available')}</Alert>
        </CardContent>
      </Card>
    );
  }

  const { points: chartData, swingMarkers, sanitized, spanDays, displayConfig } = chartPoints;
  const priorityMarkers = getPriorityExtremeMarkers(sanitized, spanDays);
  const swingDates = swingMarkers.map((point) => point.trade_date);
  const chartDataWithPins = injectPinnedDates(chartData, sanitized, swingDates);
  const xAxisTicks = getXAxisTicksForChart(chartDataWithPins, swingMarkers, spanDays, sanitized);
  const labelMetaByDate = buildLabelMetaByDate(
    swingMarkers.filter((point) => xAxisTicks.includes(point.trade_date)),
    priorityMarkers.filter((point) => xAxisTicks.includes(point.trade_date)),
  );
  const vnindexDomain = getVnindexDomain(chartDataWithPins);

  const renderXAxisTick = ({ x, y, payload }) => {
    const meta = labelMetaByDate[payload.value];
    const kind = meta?.kind;
    const fill = getTickLabelColor(kind, theme.palette.text.secondary);
    const labelY = y + 8;

    return (
      <text
        x={x}
        y={labelY}
        fill={fill}
        fontSize={kind ? 11 : 10}
        fontWeight={kind ? 700 : 500}
        textAnchor="end"
        transform={`rotate(-32, ${x}, ${labelY})`}
      >
        {formatXAxisTickForChart(payload.value, spanDays, Boolean(kind))}
      </text>
    );
  };

  return (
    <Card ref={exportRef} sx={{ borderRadius: 2, boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)' }}>
      {renderHeader()}
      <CardContent sx={{
        pt: 0.75,
        '&:last-child': { pb: 2 },
        opacity: (refreshing || calculating) ? 0.5 : 1,
        transition: 'opacity 0.15s ease',
      }}>
        <ResponsiveContainer width="100%" height={displayConfig.chartHeight}>
          <ComposedChart
            data={chartDataWithPins}
            margin={{ top: 5, right: 36, left: 0, bottom: 4 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#ddd" />

            <XAxis
              dataKey="trade_date"
              ticks={xAxisTicks}
              interval={0}
              minTickGap={8}
              height={50}
              tick={renderXAxisTick}
            />

            <YAxis
              yAxisId="left"
              label={{ value: 'VNINDEX', angle: -90, position: 'insideLeft' }}
              tick={{ fontSize: 11 }}
              domain={vnindexDomain}
              tickFormatter={(val) => `${(val / 1000).toFixed(1)}k`}
            />

            <YAxis
              yAxisId="right"
              orientation="right"
              width={58}
              label={{ content: renderRightAxisLabel }}
              tick={{ fontSize: 11 }}
              domain={[0, 1]}
              tickFormatter={(val) => `${(val * 100).toFixed(0)}%`}
            />

            <Tooltip content={<VNIndexTooltip theme={theme} t={t} />} />

            <Legend
              verticalAlign="top"
              height={28}
              wrapperStyle={{ paddingBottom: '6px' }}
            />

            <Line
              yAxisId="left"
              type="monotone"
              dataKey="vnindex_close"
              stroke={theme.palette.primary.main}
              name={t('sentiment.behavior.vnindex.legend_vnindex', 'VNINDEX')}
              dot={false}
              strokeWidth={displayConfig.vnindexStrokeWidth}
              isAnimationActive={false}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="panic_score"
              stroke="#ff9800"
              name={t('sentiment.behavior.similar_days.panic', 'Panic')}
              dot={false}
              strokeWidth={displayConfig.panicStrokeWidth}
              strokeDasharray="5 5"
              isAnimationActive={false}
            />

            <Line
              yAxisId="right"
              type="monotone"
              dataKey="fomo_score"
              stroke="#00bcd4"
              name={t('sentiment.behavior.similar_days.fomo', 'FOMO')}
              dot={false}
              strokeWidth={displayConfig.fomoStrokeWidth}
              strokeDasharray="5 5"
              isAnimationActive={false}
            />

            {displayConfig.showSwingMarkers && swingMarkers.map((point) => (
              <ReferenceDot
                key={`${point.swing_type}-${point.trade_date}`}
                x={point.trade_date}
                y={point.vnindex_close}
                yAxisId="left"
                r={getSwingMarkerRadius(spanDays)}
                fill={point.swing_type === 'bottom' ? '#4caf50' : '#f44336'}
                stroke="#fff"
                strokeWidth={2}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>

        {displayConfig.aggregationLabel && (
          <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', mt: 1, textAlign: 'right' }}>
            {t(
              `sentiment.behavior.vnindex.aggregated_${displayConfig.aggregationLabel}`,
              displayConfig.aggregationLabel === 'monthly'
                ? 'Chart aggregated monthly for readability'
                : 'Chart aggregated weekly for readability',
            )}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

VNIndexCorrelationChart.propTypes = {
  refreshTrigger: PropTypes.number,
  defaultRange: PropTypes.oneOf(['1Y', '3Y', '5Y', 'ALL']),
  calculating: PropTypes.bool,
  onCalculate: PropTypes.func,
  t: PropTypes.func.isRequired,
};

VNIndexCorrelationChart.defaultProps = {
  refreshTrigger: 0,
  defaultRange: '1Y',
  calculating: false,
  onCalculate: null,
};

export default VNIndexCorrelationChart;
